import { Component, computed, inject, signal } from '@angular/core';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ApiService } from '../../api.service';
import { InputTextModule } from 'primeng/inputtext';
import { DatePipe } from '@angular/common';
import { DialogService } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TAGEvent } from '../../../models/tagevent';
import { EventEditDialog } from './eventEditDialog';
import { EventListitemComponent } from '../../components/event-listitem/event-listitem.component';
import { TimeSpanPipe } from '../../timespan.pipe';
import { TAGFinanceTransaction } from '../../../models/tagfinances';
import { TAGInvTransaction } from '../../../models/taginventory';
import { TAGImageCategory } from '../../../models/tagimagecategory';
import { TAGImage } from '../../../models/tagimage';
import { Socket } from 'ngx-socket-io';
import { dialogConfig } from '../../dialogs';
import { NgxMaskPipe } from 'ngx-mask';
import { FinanceTransactionListItemComponent } from '../../components/finance-transaction-list-item/finance-transaction-list-item';
import { MessageService } from 'primeng/api';
import { FinanceTransactionEditDialog } from '../internal-finances/financeTransactionEditDialog';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import { DocumentGridComponent } from '../../components/document-grid/document-grid';
import { environment } from '../../../environments/environment';
import { InvTransactionListItemComponent } from '../../components/inv-transaction-list-item/inv-transaction-list-item';
import { Router } from '@angular/router';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';
import { InvTransactionImportDialog } from './InvTransactionImportDialog';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import {
  TransactionEditDialog,
  TransactionEditDialogData,
} from '../internal-inventory/transaction-edit-dialog/transactionEditDialog';
import {
  TransactionCreateDialog,
  TransactionCreateDialogData,
} from '../internal-inventory/transaction-create-dialog/transaction-create-dialog';
import { Masonry } from '../../components/masonry/masonry';
import {
  ImageDetailsDialog,
  ImageDetailsDialogData,
} from '../../components/image-details-dialog/image-details-dialog';
import { mapDateToSchoolyear } from '../../utils';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-internal-events',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    TableModule,
    SkeletonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    DatePipe,
    ButtonModule,
    TranslatePipe,
    EventListitemComponent,
    TimeSpanPipe,
    NgxMaskPipe,
    FinanceTransactionListItemComponent,
    DocumentGridComponent,
    InvTransactionListItemComponent,
    MatIcon,
    Masonry,
    FormsModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-events.html',
  styleUrl: './internal-events.scss',
})
export class InternalEvents {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private socket = inject(Socket);
  public api = inject(ApiService);

  loading = signal(true);
  allEvents = signal<TAGEvent[]>([]);

  allSchoolyears = computed(() => {
    let years = new Set<number>();
    for (let e of this.allEvents()) {
      years.add(mapDateToSchoolyear(e.startdate));
    }

    return Array.from(years.values()).sort((a, b) => b - a);
  });
  selectedSchoolyear = signal<number>(0);

  events = computed(() => {
    if (this.loading()) {
      return Array.from({ length: 3 }).map((_, i) => new TAGEvent());
    }

    return this.allEvents().filter((e) =>
      new Date().getTime() <= e.enddate.getTime() &&
      mapDateToSchoolyear(e.startdate) == this.selectedSchoolyear()
    );
  });
  oldEvents = computed(() => {
    if (this.loading()) {
      return Array.from({ length: 3 }).map((_, i) => new TAGEvent());
    }

    return this.allEvents().filter((e) =>
      new Date().getTime() > e.enddate.getTime() &&
      mapDateToSchoolyear(e.startdate) == this.selectedSchoolyear()
    );
  });

  selectedEvent = signal<TAGEvent>(undefined);
  loadingFinances = signal(false);
  eventFinances = signal<TAGFinanceTransaction[]>([]);
  loadingInventory = signal(false);
  eventInventory = signal<TAGInvTransaction[]>([]);
  loadingImages = signal(false);
  eventCategory = signal<TAGImageCategory>(undefined);
  eventImages = signal<TAGImage[]>([]);

  searchFilter = signal('');
  filteredEvents = computed(() => {
    return this.filterEvents(this.events(), this.searchFilter());
  });
  filteredOldEvents = computed(() => {
    return this.filterEvents(this.oldEvents(), this.searchFilter());
  });

  getDocumentUrl(eventID: string) {
    return environment.apiUrl + 'events/' + eventID + '/file/<filename>';
  }

  filterEvents(allEvents: TAGEvent[], filter: string) {
    if (filter.trim() == '') {
      return allEvents;
    }

    return allEvents.filter((t) => {
      return (
        t.name.toLowerCase().includes(filter.toLowerCase()) ||
        t.location.toLowerCase().includes(filter.toLowerCase()) ||
        t.description.toLowerCase().includes(filter.toLowerCase()) ||
        t.contact.toLowerCase().includes(filter.toLowerCase()) ||
        t.contactMail.toLowerCase().includes(filter.toLowerCase()) ||
        t.equipment.toLowerCase().includes(filter.toLowerCase())
      );
    });
  }

  constructor() { }

  eventTimespan(start: Date, end: Date) {
    let startTime = start.getTime();
    let endTime = end.getTime();
    return (endTime - startTime) / 1000;
  }

  anyFinancePending() {
    return this.eventFinances().find((f) => f.pending) != undefined;
  }

  financeTotal() {
    var total = 0;
    this.eventFinances().forEach((t) => {
      let nAmount = typeof t.amount == 'string' ? parseFloat(t.amount) : t.amount;
      total += t.isIncome ? nAmount : -nAmount;
    });
    return total;
  }

  ngAfterViewInit() {
    this.api.waitForAPI().then(() => {
      this.loadEvents();

      this.socket.on('loginInv', (data) => {
        if (!data.success) {
          console.error('Failed to login to inventory socket: ' + data.message);
        } else {
          this.initSocketEvents();
        }
      });

      this.socket.emit('loginInv', { token: this.api.token });
    });
  }

  ngOnDestroy(): void {
    this.socket.removeListener('updateTransaction', this.socket_onUpdateTransaction.bind(this));
    this.socket.removeListener('createTransaction', this.socket_onCreateTransaction.bind(this));
  }

  initSocketEvents() {
    this.socket.on('updateTransaction', this.socket_onUpdateTransaction.bind(this));
    this.socket.on('createTransaction', this.socket_onCreateTransaction.bind(this));
  }

  socket_onUpdateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    let currentInv = this.eventInventory();

    let tIndex = currentInv.findIndex((t) => t._id == newTrans._id);
    if (tIndex != -1) {
      currentInv[tIndex] = newTrans;
    }

    this.eventInventory.set(currentInv);
  }

  socket_onCreateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    let currentInv = this.eventInventory();

    if (newTrans.event == this.selectedEvent()._id) {
      currentInv.unshift(newTrans);
    }

    this.eventInventory.set(currentInv);
  }

  loadEvents() {
    this.loading.set(true);
    //this.events.set(Array.from({ length: 3 }).map((_, i) => new TAGEvent()));

    this.api
      .getEvents()
      .then((events: TAGEvent[]) => {
        let newEvents = events.sort((a, b) => a.startdate.getTime() - b.startdate.getTime());

        console.log(
          new Date().getTime(),
          newEvents[0].enddate.getTime(),
          new Date().getTime() >= newEvents[0].enddate.getTime(),
        );

        this.allEvents.set(newEvents);

        console.log(this.selectedSchoolyear());
        if (this.selectedSchoolyear() == 0) {
          this.selectedSchoolyear.set(this.allSchoolyears()[0]);
        }

        console.log(this.selectedSchoolyear());

        if (this.selectedEvent()) {
          let lastSelected = this.allEvents().find((e) => e._id === this.selectedEvent()._id);

          if (!lastSelected) {
            this.selectEvent();
          } else {
            this.selectEvent(lastSelected);
            this.selectedSchoolyear.set(mapDateToSchoolyear(lastSelected.startdate));
          }
        }

        this.loading.set(false);

        console.log(newEvents);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate("errors.couldNotLoadEvents")(),
          detail: reason.message,
        });
      });
  }

  selectEvent(event?: TAGEvent) {
    this.selectedEvent.set(event);
    console.log('Selecting Event: ', event?.name);

    if (this.selectedEvent()) {
      this.loadEventFinances();
      this.loadEventInventory();
      this.loadEventGallery();
    }
  }

  loadEventFinances() {
    this.loadingFinances.set(true);

    this.api
      .getFinanceTransactionsByEvent(this.selectedEvent()._id)
      .then((transactions: TAGFinanceTransaction[]) => {
        this.eventFinances.set(transactions);
        this.loadingFinances.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.translate("errors.couldNotLoadFinanceTransactions")(),
          detail: reason.message,
        });
      });
  }

  loadEventInventory() {
    this.loadingInventory.set(true);

    this.api
      .getInvTransactionsByEvent(this.selectedEvent()._id)
      .then((transactions: TAGInvTransaction[]) => {
        this.eventInventory.set(transactions);
        this.loadingInventory.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.translate("errors.couldNotLoadInventoryTransactions")(),
          detail: reason.message,
        });
      });
  }

  loadEventGallery() {
    this.loadingImages.set(true);

    this.api
      .getImageCategoryForEvent(this.selectedEvent()._id)
      .then((imageCategory: TAGImageCategory) => {
        this.eventCategory.set(imageCategory);
        this.api
          .getImages(this.eventCategory()._id)
          .then((images: TAGImage[]) => {
            this.eventImages.set(images);
            this.loadingImages.set(false);
          })
          .catch((reason) => {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.translate("errors.couldNotLoadImages")(),
              detail: reason.message,
            });
          });
      })
      .catch((reason) => {
        if (reason.status == 404) {
          this.eventCategory.set(undefined);
          this.eventImages.set([]);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate("errors.couldNotLoadImageCategory")(),
            detail: reason.message,
          });
        }
      });
  }

  openImageDetails(image: TAGImage) {
    const dialogRef = this.dialogService.open(
      ImageDetailsDialog,
      dialogConfig<ImageDetailsDialogData>({
        data: {
          image: image,
          useAuthImagePipe: true
        },
        closable: true,
        showHeader: false,
        closeOnEscape: true,
        dismissableMask: true,
      }),
    );
  }

  startInvoice() {
    this.router.navigate(['/internal/invoice'], {
      state: {
        reason: this.selectedEvent().name,
        //date: this.selectedEvent.enddate,
        user: this.api.user().username,
        eventid: this.selectedEvent()._id,
        availableItems: this.eventFinances().filter((t) => !t.isIncome),
      },
    });
  }

  getMailToLink(event: TAGEvent) {
    return (
      'mailto:' +
      event.contactMail +
      '?subject=' +
      encodeURI(event.name + '&cc=info@technikflg.com')
    );
  }

  newFinanceTransaction() {
    let newTrans = new TAGFinanceTransaction();
    newTrans.event = this.selectedEvent()._id;

    const dialogRef = this.dialogService.open(
      FinanceTransactionEditDialog,
      dialogConfig<{ transaction: TAGFinanceTransaction; }>({
        data: {
          transaction: newTrans,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) this.loadEvents();
    });
  }

  editFinanceTransaction(transaction: TAGFinanceTransaction) {
    const dialogRef = this.dialogService.open(
      FinanceTransactionEditDialog,
      dialogConfig<{ transaction: TAGFinanceTransaction; }>({
        data: {
          transaction: transaction,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) this.loadEvents();
    });
  }

  deleteFinanceTransaction(transaction: TAGFinanceTransaction) {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.finances.deleteTransactionTitle', {
          transaction: transaction,
        })(),
        this.translate.translate('internal.finances.deleteTransactionMessage', {
          transaction: transaction,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.finances.deleteTransactionProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteFinanceTransaction(transaction._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.loadEventFinances();
            await pDialog.finishDialog();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  showTransaction(transactionId: string) {
    const transactionEditRef = this.dialogService.open(
      TransactionEditDialog,
      dialogConfig<TransactionEditDialogData>({
        closable: true,
        data: {
          transactionId: transactionId,
          socket: this.socket,
        },
      }),
    );

    transactionEditRef.onClose.subscribe((result) => {
      this.loadEventInventory();
    });
  }

  unlinkTransaction(transaction: TAGInvTransaction) {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.events.unlinkTitle', {
          transaction: transaction,
          event: this.selectedEvent(),
        })(),
        this.translate.translate('internal.events.unlinkMessage', {
          transaction: transaction,
          event: this.selectedEvent(),
        })(),
        ActionConfirmationInfo.yesno_ywarn,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        transaction.event = '';

        let unlinkStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.events.unlinkProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [unlinkStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          unlinkStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.updateTransaction(transaction);
            unlinkStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.loadEventInventory();
            await pDialog.finishDialog();
          } catch (reason) {
            unlinkStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            unlinkStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  openCreateTransactionDialog() {
    const dialogRef = this.dialogService.open(
      TransactionCreateDialog,
      dialogConfig<TransactionCreateDialogData>({
        closable: true,
        data: {
          eventid: this.selectedEvent()._id,
          transactionName: this.selectedEvent().name,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.api
          .createTransaction(result)
          .then((transaction) => {
            this.showTransaction(transaction._id);
          })
          .catch((reason) => {
            console.error(reason);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.translate("errors.couldNotCreateTransaction")(),
              detail: reason.message,
            });
          });
      }
    });
  }

  openBookingImportDialog() {
    const dialogRef = this.dialogService.open(InvTransactionImportDialog, dialogConfig({}));

    dialogRef.onClose.subscribe((result) => {
      if (result != undefined) {
        let transaction: TAGInvTransaction = result;
        transaction.event = this.selectedEvent()._id;

        let importStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.events.importInvProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [importStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          importStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.updateTransaction(transaction);
            importStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.loadEventInventory();
            await pDialog.finishDialog();
          } catch (reason) {
            importStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            importStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  editEvent(event?: TAGEvent) {
    const dialogRef = this.dialogService.open(
      EventEditDialog,
      dialogConfig<{ event: TAGEvent; }>({
        data: {
          event: event,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      console.log(result);
      if (result) {
        this.loadEvents();
      }
    });
  }

  uploadAttachment(f: { fileName: string, mimeType: string, fileData: string; }) {
    console.log("kock");
    let uploadStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate('internal.document-viewer.uploadAttachmentProgress')())
        .setShowProgress(true)
        .setProgress(0),
    );

    let progressDialog = openProgressDialog(this.dialogService, [uploadStep]);

    progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
      uploadStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      try {
        await this.api.uploadEventAttachment(
          this.selectedEvent()._id,
          f.fileName,
          f.fileData,
          (progress) => {
            uploadStep.update((s) => s.setProgress(progress));
          },
        );

        uploadStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
        this.loadEvents();
        //this.selectEvent(this.selectedEvent());
        await pDialog.finishDialog();
      } catch (reason) {
        console.log(reason);
        uploadStep.update((s) =>
          s.setState(ProgressDialogStepState.ERROR).setSubtitle(reason.message),
        );
        pDialog.finishDialog(-1);
      }
    });
  }

  deleteAttachment(filename: string) {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.document-viewer.deleteAttachmentTitle', {
          filename: filename,
        })(),
        this.translate.translate('internal.document-viewer.deleteAttachmentMessage', {
          filename: filename,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.document-viewer.deleteAttachmentProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteEventAttachment(this.selectedEvent()._id, filename);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.selectedEvent.update((e) => {
              let newevt = new TAGEvent(e.serialize());
              newevt.files = newevt.files.filter((f) => f.fileName !== filename);
              return newevt;
            });
            this.loadEvents();
            await pDialog.finishDialog();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }
}
